import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { getWorkspaceImageBlob, tenantKeys } from './tenancyApi';

/**
 * Workspace image tile with an initial-letter fallback. The image is fetched
 * as an authenticated blob (bearer header — a plain <img src> can't send it);
 * `imageKey` in the query key doubles as the cache buster since every upload
 * mints a fresh storage key.
 *
 * `previewUrl` short-circuits the fetch entirely — pass a local object URL to
 * show a just-picked file while its upload is still in flight.
 */
export default function WorkspaceAvatar({ orgId, workspace, className, previewUrl = null }) {
  const { data: blob } = useQuery({
    queryKey: tenantKeys.workspaceImage(workspace?.id, workspace?.imageKey),
    queryFn: () =>
      getWorkspaceImageBlob({
        orgId,
        workspaceId: workspace.id,
        imageKey: workspace.imageKey,
      }),
    enabled: Boolean(orgId && workspace?.id && workspace?.imageKey) && !previewUrl,
    staleTime: Infinity,
  });

  // Create AND revoke the object URL inside one effect so they always pair up.
  // (A useMemo-created URL survives StrictMode's mount→cleanup→remount cycle
  // after its cleanup revoked it → broken images on reopened dropdowns/modals.)
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [blob]);

  const classes = clsx(
    'inline-flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-md',
    className,
  );

  const src = previewUrl ?? url;
  if (src) {
    // object-contain, not object-cover: a wide wordmark is the common logo shape and
    // cropping one beheads it. Letterboxing inside the tile is the kinder failure.
    //
    // bg-white (not the themed slate-*) because logos are usually transparent PNGs
    // drawn for a light ground — on a dark surface the dark artwork disappears.
    // Same reasoning as the PDF page surface in signatures/components/PdfPage.jsx.
    return <img src={src} alt="" className={clsx(classes, 'bg-white object-contain')} />;
  }
  return (
    <span className={clsx(classes, 'bg-brand-600/10 text-[11px] font-semibold text-brand-600')}>
      {(workspace?.name ?? '?').charAt(0).toUpperCase()}
    </span>
  );
}
